import { ReactNode, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';

import { MinimalPlateformeDto } from 'api/plateforme';
import { AppInitLoader } from 'components/ui/AppInitLoader';
import { plateformesAPI } from 'services/swagger-api';
import { useCurrentPlateforme } from 'shared/contexts/app-init';

import { PlateformeChooser } from './PlateformeChooser';
import { PLATEFORME_CHOICE_PENDING_KEY, markChoiceDone } from './plateformeChoiceMarker';

const frontUrl = process.env.REACT_APP_FRONT_URL;

type GateState =
  | { status: 'pending' }
  | { status: 'choice'; plateformes: MinimalPlateformeDto[] }
  | { status: 'done' };

interface PlateformeChoiceGateProps {
  children: ReactNode;
}

/**
 * Après une connexion standard : déclare la connexion au back, qui écarte les
 * plateformes en SSO partenaire. Compte dont toutes les plateformes sont en SSO
 * partenaire → déconnexion vers /login-error?reason=partenaire ; plusieurs
 * plateformes standard → écran de choix ; sinon → entrée directe.
 */
export const PlateformeChoiceGate = ({ children }: PlateformeChoiceGateProps) => {
  const auth = useAuth();
  const currentPlateforme = useCurrentPlateforme();
  const [state, setState] = useState<GateState>(() =>
    sessionStorage.getItem(PLATEFORME_CHOICE_PENDING_KEY) ? { status: 'pending' } : { status: 'done' },
  );
  const requestedRef = useRef(false);

  useEffect(() => {
    if (state.status !== 'pending' || requestedRef.current) return;
    requestedRef.current = true;

    const declare = async () => {
      try {
        const response = await plateformesAPI.declarerConnexionStandard();

        if (response.accesRefuse) {
          localStorage.clear();
          sessionStorage.clear();
          await auth.signoutRedirect({
            post_logout_redirect_uri: `${frontUrl}/login-error?reason=partenaire`,
          });
          return;
        }

        if (response.choixRequis) {
          setState({ status: 'choice', plateformes: response.plateformes });
          return;
        }

        markChoiceDone(auth.user?.profile.sub);

        // Le back a réaligné la plateforme active (ex. session SSO précédente) :
        // l'état d'AppInit est obsolète, on recharge sur la destination prévue.
        if (response.currentPlateforme && response.currentPlateforme.id !== currentPlateforme?.id) {
          sessionStorage.removeItem(PLATEFORME_CHOICE_PENDING_KEY);
          window.location.replace(window.location.pathname + window.location.search);
          return;
        }
      } catch (error) {
        // Échec de la déclaration : on n'empêche pas l'entrée (plateforme active
        // résolue par le back comme avant).
        console.error('[connexion standard]', error);
      }

      sessionStorage.removeItem(PLATEFORME_CHOICE_PENDING_KEY);
      setState({ status: 'done' });
    };

    declare();
  }, [state.status, auth, currentPlateforme?.id]);

  if (state.status === 'pending') {
    return <AppInitLoader />;
  }

  if (state.status === 'choice') {
    return <PlateformeChooser plateformes={state.plateformes} />;
  }

  return <>{children}</>;
};
